/**
 * useQuota — hook partagé pour récupérer l'état du quota de sessions du user
 * courant (= user impersoné si applicable, géré au niveau apiFetch).
 *
 * Cf. spec/rate-limiter-sessions.md §3.1
 */
import { useEffect, useState } from "react";
import { trainingApi } from "../lib/api";

export interface QuotaState {
  limit: number | null;
  used: number;
  remaining: number | null;
  totalPacksRemaining: number;
  // Crédits packs one-shot (grants) — compteur global compte (cf. spec packs §2.4)
  oneShotCredits?: { total: number; used: number; remaining: number };
  isKestioInternal: boolean;
  envMode: "prod" | "demo" | "staging";
  gracePeriodActive: boolean;
  allowed: boolean;
  reason?: string;
  // Optionnels (renvoyés quand allowed=false par l'API)
  ctaUrl?: string;
  cycleEnd?: string;
  // État de l'abonnement quand il n'est plus actif (résilié, en pause, terminé).
  subscriptionState?: "active" | "trial" | "cancelled" | "non_renewing" | "unpaid" | "paused" | "ended";
  subscriptionIsOpen?: boolean;
  subscriptionEndedAt?: string | null;
  subscriptionResumesAt?: string | null;
  /** `"manual"` : licence d'abonnement posée à la main par Kestio, arrivée à expiration. */
  subscriptionSource?: "manual";
}

export function useQuota(): { quota: QuotaState | null; loading: boolean } {
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    trainingApi.getQuota()
      .then((q) => { if (!cancelled) setQuota(q as QuotaState); })
      .catch(() => { /* silencieux — pas de crash UI si endpoint indispo */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { quota, loading };
}

/**
 * Helpers de classification d'état pour les composants.
 *
 * - "exhausted" : l'user ne peut plus lancer de session (quota plan + packs épuisés
 *   et pas de grant custom illimité). C'est l'état qui doit alerter visuellement.
 * - "warning" : quota plan dépassé mais packs encore dispo (achat passé). Pas critique.
 * - "ok" : tout va bien.
 */
export function classifyQuota(quota: QuotaState | null): "ok" | "warning" | "exhausted" | "hidden" {
  if (!quota) return "hidden";
  if (quota.isKestioInternal || quota.envMode === "staging") return "hidden";
  if (quota.limit == null) return "hidden";
  if (!quota.allowed) return "exhausted";
  const totalAvailable = (quota.limit ?? 0) + quota.totalPacksRemaining;
  if (totalAvailable === 0) return "exhausted";
  const ratio = quota.used / totalAvailable;
  if (ratio >= 0.8) return "warning";
  return "ok";
}
